import { ExternalLinkIcon, RefreshCwIcon, TriangleAlertIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useFormatters } from '@/shared/i18n/format';
import {
  APP_BUILT_AT,
  APP_COMMIT,
  APP_VERSION,
  CHANGELOG_URL,
  REPOSITORY_URL,
  commitUrl,
  isSameBuild,
  releaseUrl,
  shortCommit,
  useServerVersion,
} from '@/shared/lib/buildInfo';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Skeleton } from '@/shared/ui/skeleton';

import { OrbitaMark } from '../brand/OrbitaLogo';

const YEAR = new Date().getFullYear();

function ExternalLink({ href, children, testId }: { href: string; children: ReactNode; testId?: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 rounded-sm font-medium text-brand-text underline-offset-2 focus-ring hover:underline"
      data-testid={testId}
    >
      {children}
      <ExternalLinkIcon className="size-3.5" aria-hidden />
    </a>
  );
}

/** One component's build: version, commit (linked to the repository) and build time. */
function BuildRow({
  label,
  version,
  commit,
  builtAt,
  testId,
}: {
  label: string;
  version: string;
  commit: string | null;
  builtAt: string | null;
  testId: string;
}) {
  const { t } = useTranslation('app');
  const fmt = useFormatters();
  return (
    <div
      className="grid grid-cols-[7rem_minmax(0,1fr)] gap-x-3 gap-y-0.5 py-2.5 text-sm"
      data-testid={testId}
    >
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0">
        <span className="font-medium text-foreground tabular-nums" data-testid={`${testId}-version`}>
          {version}
        </span>
        <span className="text-muted-foreground"> · </span>
        {commit ? (
          <a
            href={commitUrl(commit)}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-sm font-mono text-xs text-brand-text underline-offset-2 focus-ring hover:underline"
            title={commit}
            data-testid={`${testId}-commit`}
          >
            {shortCommit(commit)}
          </a>
        ) : (
          <span className="text-xs text-muted-foreground" data-testid={`${testId}-commit`}>
            {t('about.localBuild')}
          </span>
        )}
        {builtAt && (
          <span className="block text-xs text-muted-foreground">
            {t('about.builtAt', { time: fmt.date(builtAt, 'dateTime') })}
          </span>
        )}
      </dd>
    </div>
  );
}

/**
 * "About" dialog of the user menu: the SPA's build and the API's (`GET /api/version`), a hint when they differ (a tab
 * opened before a deployment), and links to the release notes, the changelog and the repository.
 */
export function AboutDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('app');
  const server = useServerVersion({ enabled: open });
  const web = { version: APP_VERSION, commit: APP_COMMIT };
  const mismatch = server.data !== undefined && !isSameBuild(web, server.data);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm" data-testid="about-dialog">
        <DialogHeader className="flex-row items-center gap-3">
          <OrbitaMark className="size-11" />
          <div className="min-w-0 space-y-0.5">
            <DialogTitle>
              Orbita{' '}
              <span className="font-normal text-muted-foreground" data-testid="about-version">
                {t('about.version', { version: APP_VERSION })}
              </span>
            </DialogTitle>
            <DialogDescription>{t('about.description')}</DialogDescription>
          </div>
        </DialogHeader>

        <dl className="divide-y rounded-lg border px-3">
          <BuildRow
            label={t('about.web')}
            version={APP_VERSION}
            commit={APP_COMMIT}
            builtAt={APP_BUILT_AT}
            testId="about-web"
          />
          {server.data ? (
            <BuildRow
              label={t('about.server')}
              version={server.data.version}
              commit={server.data.commit}
              builtAt={server.data.builtAt}
              testId="about-api"
            />
          ) : (
            <div
              className="grid grid-cols-[7rem_minmax(0,1fr)] gap-x-3 py-2.5 text-sm"
              data-testid="about-api"
            >
              <dt className="text-muted-foreground">{t('about.server')}</dt>
              <dd>
                {server.isError ? (
                  <span className="text-muted-foreground">{t('about.serverUnavailable')}</span>
                ) : (
                  <Skeleton className="h-4 w-28" />
                )}
              </dd>
            </div>
          )}
        </dl>

        {mismatch && (
          <Alert variant="warning" data-testid="about-mismatch">
            <TriangleAlertIcon />
            <AlertDescription className="text-foreground">
              {t('about.mismatch')}
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => {
                  window.location.reload();
                }}
              >
                <RefreshCwIcon />
                {t('about.reload')}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm">
          <ExternalLink href={releaseUrl(APP_VERSION)} testId="about-release-notes">
            {t('about.releaseNotes')}
          </ExternalLink>
          <ExternalLink href={CHANGELOG_URL}>{t('about.changelog')}</ExternalLink>
          <ExternalLink href={REPOSITORY_URL}>{t('about.source')}</ExternalLink>
        </div>

        <p className="text-xs text-subtle-foreground">© {YEAR} Orbita</p>
      </DialogContent>
    </Dialog>
  );
}
